import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plus, Pencil, Trash2, Eye, EyeOff, Upload, Download, Search } from "lucide-react";

const emptyStudent = { first_name: "", last_name: "", number: "", tc_no: "", grade: "8", branch: "A", parent_name: "", parent_email: "", parent_phone: "", active: true };

export default function Students() {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [filterClass, setFilterClass] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [csvText, setCsvText] = useState("");

  const load = () => {
    const params = {};
    if (filterClass !== "all") { const [g, b] = filterClass.split("-"); params.grade = g; params.branch = b; }
    if (q) params.q = q;
    api.get("/students", { params }).then((r) => setStudents(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterClass, q]);

  const openNew = () => { setEditing({ ...emptyStudent }); setOpen(true); };
  const openEdit = (s) => { setEditing({ ...emptyStudent, ...s, tc_no: s.tc_no || "" }); setOpen(true); };

  const save = async () => {
    if (!editing.first_name || !editing.number) { toast.error("Ad ve numara gerekli"); return; }
    try {
      if (editing.id) await api.put(`/students/${editing.id}`, editing);
      else await api.post("/students", editing);
      toast.success("Öğrenci kaydedildi"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/students/${id}`); toast.success("Öğrenci silindi"); load(); } catch (e) { toast.error(apiError(e)); } };

  const reveal = async (id) => {
    if (revealed[id]) { setRevealed((r) => ({ ...r, [id]: null })); return; }
    try { const { data } = await api.get(`/students/${id}/reveal-tc`); setRevealed((r) => ({ ...r, [id]: data.tc_no || "—" })); }
    catch (e) { toast.error(apiError(e)); }
  };

  const parseCsv = (text) => {
    const alias = { adi: "first_name", soyadi: "last_name", "ogrenci_no": "number", "öğrenci_no": "number", tc_kimlik_no: "tc_no", "tc_kimlik": "tc_no", sinif: "grade", "sınıf": "grade", sube: "branch", "şube": "branch", veli_adi: "parent_name", veli_eposta: "parent_email", veli_telefon: "parent_phone", first_name: "first_name", last_name: "last_name", number: "number", tc_no: "tc_no", grade: "grade", branch: "branch", parent_name: "parent_name", parent_email: "parent_email", parent_phone: "parent_phone" };
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];
    const rawHeaders = lines[0].split(/[,;\t]/).map((h) => h.trim().toLowerCase());
    const headers = rawHeaders.map((h) => alias[h] || h);
    return lines.slice(1).map((line) => {
      const cells = line.split(/[,;\t]/);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = (cells[i] || "").trim(); });
      return obj;
    });
  };

  const doImport = async () => {
    const rows = parseCsv(csvText);
    if (!rows.length) { toast.error("Geçerli satır bulunamadı"); return; }
    try {
      const { data } = await api.post("/students/bulk", { students: rows });
      toast.success(`${data.created} öğrenci eklendi${data.errors.length ? `, ${data.errors.length} hata` : ""}`);
      if (data.errors.length) data.errors.slice(0, 3).forEach((e) => toast.warning(e));
      setImportOpen(false); setCsvText(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const onFile = async (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    // Excel (.xlsx) dosyasını doğrudan sunucuya yükle
    if (f.name.toLowerCase().endsWith(".xlsx")) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/students/import-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${data.created} öğrenci eklendi${data.errors.length ? `, ${data.errors.length} satır atlandı` : ""}`);
        if (data.errors.length) data.errors.slice(0, 3).forEach((e) => toast.warning(e));
        setImportOpen(false); load();
      } catch (e) { toast.error(apiError(e)); }
      ev.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(f);
  };

  return (
    <div>
      <PageHeader title="Öğrenciler" desc={`${students.length} öğrenci listeleniyor`}>
        <Button variant="outline" onClick={() => window.open(`${API}/students/template.xlsx`, "_blank")} data-testid="csv-template"><Download className="h-4 w-4 mr-2" />Excel Şablon İndir</Button>
        <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="csv-import"><Upload className="h-4 w-4 mr-2" />CSV İçe Aktar</Button>
        <Button variant="outline" onClick={() => window.open(`${API}/students/export.xlsx`, "_blank")} data-testid="xlsx-export"><Download className="h-4 w-4 mr-2" />Excel Dışa Aktar</Button>
        <Button onClick={openNew} data-testid="student-add"><Plus className="h-4 w-4 mr-2" />Öğrenci Ekle</Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad, soyad veya numara ara..." className="pl-9" data-testid="student-search" />
        </div>
        <Select value={filterClass} onValueChange={setFilterClass}>
          <SelectTrigger className="w-full sm:w-48" data-testid="student-class-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">Tüm Sınıflar</SelectItem>
            {classes.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {students.length === 0 ? (
        <EmptyState icon={Users} title="Öğrenci bulunamadı" desc="Öğrenci ekleyin veya CSV ile toplu aktarım yapın.">
          <Button onClick={openNew}>Öğrenci Ekle</Button>
        </EmptyState>
      ) : (
        <Card className="hidden md:block bg-card border-border overflow-hidden">
          <div className="overflow-x-auto ktt-scroll">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr><th className="text-left p-3">No</th><th className="text-left p-3">Ad Soyad</th><th className="text-left p-3">Sınıf</th>
                  <th className="text-left p-3">T.C. Kimlik</th><th className="text-left p-3">Veli Tel</th><th className="text-left p-3">Durum</th><th className="p-3"></th></tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.id} className="border-t border-border hover:bg-muted/30" data-testid={`student-row-${s.number}`}>
                    <td className="p-3 font-mono">{s.number}</td>
                    <td className="p-3 font-medium">{s.first_name} {s.last_name}</td>
                    <td className="p-3"><Badge variant="secondary">{s.grade}-{s.branch}</Badge></td>
                    <td className="p-3 font-mono text-xs">
                      <div className="flex items-center gap-1.5">
                        {revealed[s.id] || s.tc_no_masked}
                        <button onClick={() => reveal(s.id)} data-testid={`tc-toggle-${s.number}`} className="text-muted-foreground hover:text-foreground">
                          {revealed[s.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{s.parent_phone || "—"}</td>
                    <td className="p-3">{s.active ? <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">Aktif</Badge> : <Badge variant="secondary">Pasif</Badge>}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`student-edit-${s.number}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => del(s.id)} data-testid={`student-delete-${s.number}`}><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {students.length > 0 && (
        <div className="md:hidden space-y-2.5">
          {students.map((s) => (
            <Card key={s.id} className="p-3 bg-card border-border" data-testid={`student-card-${s.number}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{s.first_name} {s.last_name}</div>
                  <div className="flex items-center flex-wrap gap-2 mt-1">
                    <Badge variant="secondary">{s.grade}-{s.branch}</Badge>
                    <span className="font-mono text-xs text-muted-foreground">No {s.number}</span>
                    {s.active ? <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">Aktif</Badge> : <Badge variant="secondary">Pasif</Badge>}
                  </div>
                  {s.parent_phone && <div className="text-xs text-muted-foreground mt-1.5">Veli Tel: {s.parent_phone}</div>}
                </div>
                <div className="flex shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`student-edit-m-${s.number}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => del(s.id)} data-testid={`student-delete-m-${s.number}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto ktt-scroll">
          <DialogHeader><DialogTitle>{editing?.id ? "Öğrenci Düzenle" : "Yeni Öğrenci"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Ad *</Label><Input value={editing.first_name} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} data-testid="s-first" /></div>
              <div className="space-y-1.5"><Label>Soyad</Label><Input value={editing.last_name} onChange={(e) => setEditing({ ...editing, last_name: e.target.value })} data-testid="s-last" /></div>
              <div className="space-y-1.5"><Label>Okul No *</Label><Input value={editing.number} onChange={(e) => setEditing({ ...editing, number: e.target.value })} data-testid="s-number" /></div>
              <div className="space-y-1.5"><Label>T.C. Kimlik No</Label><Input value={editing.tc_no} onChange={(e) => setEditing({ ...editing, tc_no: e.target.value })} data-testid="s-tc" /></div>
              <div className="space-y-1.5"><Label>Sınıf</Label><Input value={editing.grade} onChange={(e) => setEditing({ ...editing, grade: e.target.value })} data-testid="s-grade" /></div>
              <div className="space-y-1.5"><Label>Şube</Label><Input value={editing.branch} onChange={(e) => setEditing({ ...editing, branch: e.target.value })} data-testid="s-branch" /></div>
              <div className="space-y-1.5"><Label>Veli Adı</Label><Input value={editing.parent_name} onChange={(e) => setEditing({ ...editing, parent_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Veli E-posta</Label><Input value={editing.parent_email} onChange={(e) => setEditing({ ...editing, parent_email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Veli Telefon</Label><Input value={editing.parent_phone} onChange={(e) => setEditing({ ...editing, parent_phone: e.target.value })} /></div>
              <div className="flex items-center gap-2 pt-6"><Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="s-active" /><Label>Aktif</Label></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button><Button onClick={save} data-testid="s-save">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-card border-border max-w-xl max-h-[90vh] overflow-y-auto ktt-scroll">
          <DialogHeader><DialogTitle>Öğrenci Aktar (Excel / CSV)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 p-3 rounded-lg bg-muted">
              <div className="text-sm">1. Önce şablonu indirin ve örneklere göre doldurun.</div>
              <Button size="sm" variant="outline" onClick={() => window.open(`${API}/students/template.xlsx`, "_blank")} data-testid="import-template-download"><Download className="h-4 w-4 mr-1.5" />Excel Şablon</Button>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Sütunlar böyle olmalı</div>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50"><tr><th className="text-left p-2">Sütun Adı</th><th className="text-left p-2">Ne Yazılır</th><th className="text-left p-2 w-16">Zorunlu</th></tr></thead>
                  <tbody className="divide-y divide-border">
                    <tr><td className="p-2 font-mono">Adi</td><td className="p-2">Ahmet</td><td className="p-2 text-emerald-600 font-semibold">Evet</td></tr>
                    <tr><td className="p-2 font-mono">Soyadi</td><td className="p-2">Yılmaz</td><td className="p-2 text-emerald-600 font-semibold">Evet</td></tr>
                    <tr><td className="p-2 font-mono">Ogrenci_No</td><td className="p-2">101 (sadece rakam)</td><td className="p-2 text-emerald-600 font-semibold">Evet</td></tr>
                    <tr><td className="p-2 font-mono">TC_Kimlik_No</td><td className="p-2">12345678901 (11 hane)</td><td className="p-2 text-muted-foreground">Hayır</td></tr>
                    <tr><td className="p-2 font-mono">Sinif</td><td className="p-2">sadece seviye: 5 / 6 / 7 / 8</td><td className="p-2 text-emerald-600 font-semibold">Evet</td></tr>
                    <tr><td className="p-2 font-mono">Sube</td><td className="p-2">sadece harf: A / B / C</td><td className="p-2 text-emerald-600 font-semibold">Evet</td></tr>
                    <tr><td className="p-2 font-mono">Veli_Adi</td><td className="p-2">Mehmet Yılmaz</td><td className="p-2 text-muted-foreground">Hayır</td></tr>
                    <tr><td className="p-2 font-mono">Veli_Eposta</td><td className="p-2">veli@example.com</td><td className="p-2 text-muted-foreground">Hayır</td></tr>
                    <tr><td className="p-2 font-mono">Veli_Telefon</td><td className="p-2">05001112233</td><td className="p-2 text-muted-foreground">Hayır</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">2. Dosyayı yükleyin</div>
              <Input type="file" accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFile} data-testid="csv-file" />
              <p className="text-xs text-muted-foreground mt-1.5">.xlsx (önerilen) veya .csv dosyası seçin. Excel dosyası doğrudan aktarılır.</p>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">veya CSV içeriğini yapıştırın</div>
              <textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={4} placeholder="Adi,Soyadi,Ogrenci_No,TC_Kimlik_No,Sinif,Sube,Veli_Adi,Veli_Eposta,Veli_Telefon" className="w-full rounded-md border border-border bg-background p-2 text-xs font-mono" data-testid="csv-textarea" />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setImportOpen(false)}>Vazgeç</Button><Button onClick={doImport} data-testid="csv-do-import">Metni Aktar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
